import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Box, Typography, Button, Stack, Chip, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Skeleton, Alert,
  TablePagination, alpha, useTheme, Switch, Snackbar,
} from '@mui/material';
import { Add, Edit, Delete, FileDownload, Garage } from '@mui/icons-material';
import RentalPackageDrawer, { type RentalPackageFormState } from './RentalPackageDrawer';
import { getMasters, createMaster, updateMaster, deleteMaster } from '../../services/platform/masterService';
import { listZones } from '../../services/zoneService';
import {
  checkRentalDuplicate,
  deleteRentalPackageGroup,
  listRentalPackagesByZone,
  toggleRentalPackageStatus,
} from '../../services/rentalManagementService';
import { downloadListCsv, rowsToCsv } from '../../utils/listExportCsv';
import { groupRentalPackages, mapApiRentalGroups, normalizeRentalPackageHours, normalizeRentalPackageKm, validateRentalPackageBundle, validateRentalPackageHours, validateRentalPackageKm, getDuplicateRentalPackageMessage, isRentalDuplicateApiResponse, rentalDuplicateApiMessage, validateRentalGraceMinutes, validateRentalVehiclePrice, buildRentalPackageGroupKey, patchRentalPackageGroups, type RentalPackageGroup } from '../../utils/rentalPackages';
import { parseMasterListRows } from '../../utils/masterRows';
import { useI18n } from '../../i18n/I18nProvider';
import { translateUiText } from '../../i18n/translateUiText';
import { formatMoney } from '../../utils/currency';
import { TRANSPORT_RENTAL_SERVICE_SLUG } from '../../constants/transportPartnerFlow';

function apiErrorMessage(err: unknown, fallback: string) {
  const ax = err as { response?: { data?: { message?: string } }; message?: string };
  return ax.response?.data?.message || ax.message || fallback;
}

const ALL_RENTAL_SLUGS = ['bike-rental', 'car-rental'];

interface RentalPackagesPanelProps {
  /** Lock to a single zone (Caby zone drill-down) */
  lockedZoneCode?: string;
  /** Display name for the locked zone */
  lockedZoneName?: string;
  /** Default service slug filter (hub-specific bike/car tabs only) */
  defaultServiceSlug?: string;
  /** Hide bike/car service filter chips */
  hideServiceFilter?: boolean;
  /** Unified transport rental — one screen for all services, no bike/car split */
  unifiedTransportRental?: boolean;
  /** Page title override */
  title?: string;
  subtitle?: string;
}

export default function RentalPackagesPanel({
  lockedZoneCode,
  lockedZoneName,
  defaultServiceSlug = '',
  hideServiceFilter = false,
  unifiedTransportRental = false,
  title,
  subtitle,
}: RentalPackagesPanelProps) {
  const theme = useTheme();
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const isDedicatedRental = !unifiedTransportRental && ALL_RENTAL_SLUGS.includes(defaultServiceSlug);

  const [serviceFilter, setServiceFilter] = useState(
    unifiedTransportRental
      ? 'all'
      : (isDedicatedRental ? defaultServiceSlug : (defaultServiceSlug || 'all')),
  );
  const [zoneFilter, setZoneFilter] = useState(lockedZoneCode || '');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<RentalPackageGroup | null>(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [togglingGroupKey, setTogglingGroupKey] = useState<string | null>(null);
  const [statusOverrides, setStatusOverrides] = useState<Record<string, boolean>>({});
  const [toast, setToast] = useState<{ msg: string; severity: 'success' | 'error' } | null>(null);

  const packagesQueryKey = [
    'rental-packages',
    unifiedTransportRental ? 'transport-unified' : serviceFilter,
    lockedZoneCode || zoneFilter,
  ] as const;

  const activeServiceSlug = unifiedTransportRental || serviceFilter === 'all' ? '' : serviceFilter;
  const saveServiceSlug = (group: RentalPackageGroup | null | undefined) => {
    if (group?.serviceSlug) return group.serviceSlug;
    if (unifiedTransportRental) return TRANSPORT_RENTAL_SERVICE_SLUG;
    return activeServiceSlug || defaultServiceSlug || TRANSPORT_RENTAL_SERVICE_SLUG;
  };
  const drawerServiceSlug = unifiedTransportRental
    ? TRANSPORT_RENTAL_SERVICE_SLUG
    : (activeServiceSlug || defaultServiceSlug || TRANSPORT_RENTAL_SERVICE_SLUG);

  const { data: zonesData } = useQuery({
    queryKey: ['zones-rental'],
    queryFn: () => listZones({ limit: 500 }),
  });
  const zones = zonesData?.rows || [];

  const { data: vehicleTypeRows = [] } = useQuery({
    queryKey: ['vehicle-types-rental-full'],
    queryFn: async () => {
      const res = await getMasters('vehicle_types', { limit: 200, page: 1 });
      return parseMasterListRows<{
        code: string; name: string; metadata?: { serviceTypes?: string };
      }>(res);
    },
  });

  const effectiveZone = lockedZoneCode || zoneFilter;

  const zoneNameMap = useMemo(() => {
    const map = new Map<string, string>();
    zones.forEach((z) => { if (z.code) map.set(z.code, z.name); });
    return map;
  }, [zones]);

  const drawerZones = useMemo(() => {
    if (!lockedZoneCode || zones.some((z) => z.code === lockedZoneCode)) return zones;
    const name = lockedZoneName || zoneNameMap.get(lockedZoneCode);
    if (!name) return zones;
    return [{ _id: lockedZoneCode, name, code: lockedZoneCode }, ...zones];
  }, [zones, lockedZoneCode, lockedZoneName, zoneNameMap]);

  const resolvedLockedZoneName = lockedZoneName || (lockedZoneCode ? zoneNameMap.get(lockedZoneCode) : undefined);

  const vehicleNameMap = useMemo(() => {
    const map = new Map<string, string>();
    vehicleTypeRows.forEach((v) => map.set(v.code, v.name));
    return map;
  }, [vehicleTypeRows]);

  const { data: packagesData, isLoading } = useQuery({
    queryKey: packagesQueryKey,
    queryFn: async () => {
      if (effectiveZone) {
        const res = await listRentalPackagesByZone(effectiveZone, {
          serviceSlug: activeServiceSlug || undefined,
        });
        return mapApiRentalGroups(res?.groups || [], zoneNameMap, vehicleNameMap);
      }
      const res = await getMasters('rental_packages', { limit: 500, page: 1 });
      const rows = parseMasterListRows<{
        _id: string; code: string; name: string; isActive?: boolean; metadata?: Record<string, unknown>;
      }>(res);
      return groupRentalPackages(rows, zoneNameMap, vehicleNameMap);
    },
  });

  const allGroups = useMemo(() => {
    if (effectiveZone) return packagesData || [];
    const filtered = ((packagesData || []) as RentalPackageGroup[]).filter((row) => {
      if (activeServiceSlug && row.serviceSlug && row.serviceSlug !== activeServiceSlug) return false;
      if (effectiveZone && row.zoneCode !== effectiveZone) return false;
      return true;
    });
    return filtered;
  }, [packagesData, activeServiceSlug, effectiveZone]);

  const pagedGroups = allGroups.slice(page * pageSize, page * pageSize + pageSize);

  const resolveGroupActive = (group: RentalPackageGroup) => {
    const key = buildRentalPackageGroupKey(group);
    return statusOverrides[key] ?? group.isActive;
  };

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['rental-packages'] });
    queryClient.invalidateQueries({ queryKey: ['rental-zones'] });
  };

  const saveMutation = useMutation({
    mutationFn: async ({ form, group }: { form: RentalPackageFormState; group: RentalPackageGroup | null }) => {
      const hr = normalizeRentalPackageHours(form.packageHr);
      const km = normalizeRentalPackageKm(form.packageKm);
      const hrError = validateRentalPackageHours(hr);
      const kmError = validateRentalPackageKm(km);
      const bundleError = validateRentalPackageBundle(hr, km);
      if (hrError || kmError || bundleError) {
        throw new Error(bundleError || kmError || hrError || 'Invalid package hours/KM.');
      }

      const serviceSlug = saveServiceSlug(group);
      const saveZoneCode = lockedZoneCode || form.zoneCode;

      for (const vp of form.vehiclePrices) {
        const priceError = validateRentalVehiclePrice(vp.price);
        const graceError = validateRentalGraceMinutes(vp.graceTimeMin);
        if (priceError || graceError) {
          throw new Error(priceError || graceError || 'Enter valid pricing for every vehicle type.');
        }
      }

      const duplicateMessage = getDuplicateRentalPackageMessage(allGroups, {
        zoneCode: saveZoneCode,
        packageHr: hr,
        packageKm: km,
        serviceSlug,
        excludeGroupCode: group?.packageGroupCode,
        ignoreServiceSlug: unifiedTransportRental,
      });
      if (duplicateMessage) throw new Error(duplicateMessage);

      if (!group) {
        try {
          const dupCheck = await checkRentalDuplicate({
            zoneCode: saveZoneCode,
            packageHr: hr,
            packageKm: km,
            serviceSlug,
          });
          if (isRentalDuplicateApiResponse(dupCheck)) {
            throw new Error(rentalDuplicateApiMessage(
              dupCheck,
              duplicateMessage || 'A package with the same zone, hours, and KM already exists for this service.',
            ));
          }
        } catch (err: unknown) {
          const ax = err as {
            response?: { status?: number; data?: { message?: string; data?: unknown } };
            message?: string;
          };
          if (ax.response?.status === 409) {
            throw new Error(ax.response.data?.message || duplicateMessage || 'A package with the same zone, hours, and KM already exists for this service.');
          }
          const payload = ax.response?.data?.data ?? ax.response?.data;
          if (isRentalDuplicateApiResponse(payload)) {
            throw new Error(rentalDuplicateApiMessage(
              payload,
              duplicateMessage || 'A package with the same zone, hours, and KM already exists for this service.',
            ));
          }
          if (ax.response?.status && ax.response.status !== 404) throw err;
        }
      }

      const ops = form.vehiclePrices.map((vp) => {
        const code = vp.rowCode || `${form.packageGroupCode}_${vp.vehicleTypeCode.toUpperCase()}`;
        const name = `${hr} Hours / ${km} KM — ${vp.vehicleTypeName}`;
        const payload = {
          code,
          name,
          isActive: group?.isActive ?? true,
          metadata: {
            zoneCode: saveZoneCode,
            serviceSlug,
            packageGroupCode: form.packageGroupCode,
            vehicleTypeCode: vp.vehicleTypeCode,
            packageKm: km,
            packageHr: hr,
            price: vp.price,
            graceTimeMin: vp.graceTimeMin,
            extraKmPrice: vp.extraKmPrice,
            status: (group?.isActive ?? true) ? 'active' : 'inactive',
          },
        };
        if (vp.rowId) return updateMaster(vp.rowId, payload);
        return createMaster('rental_packages', payload);
      });

      const existingCodes = new Set(form.vehiclePrices.map((v) => v.vehicleTypeCode));
      const removeOps = (group?.vehiclePrices || [])
        .filter((vp) => vp.rowId && !existingCodes.has(vp.vehicleTypeCode))
        .map((vp) => deleteMaster(vp.rowId!));

      await Promise.all([...ops, ...removeOps]);
    },
    onSuccess: () => {
      invalidate();
      setDrawerOpen(false);
      setEditingGroup(null);
      setToast({ msg: 'Package saved', severity: 'success' });
    },
    onError: (err: unknown) => {
      setToast({ msg: apiErrorMessage(err, 'Failed to save package'), severity: 'error' });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (group: RentalPackageGroup) => {
      await deleteRentalPackageGroup({
        zoneCode: group.zoneCode,
        packageGroupCode: group.packageGroupCode,
        serviceSlug: saveServiceSlug(group),
      });
    },
    onSuccess: () => {
      invalidate();
      setToast({ msg: 'Package deleted', severity: 'success' });
    },
    onError: (err: unknown) => {
      setToast({ msg: apiErrorMessage(err, 'Cannot delete package'), severity: 'error' });
    },
  });

  const toggleMutation = useMutation({
    mutationFn: async ({ group, isActive }: { group: RentalPackageGroup; isActive: boolean }) => {
      const rowIds = group.vehiclePrices.map((v) => v.rowId).filter(Boolean) as string[];
      if (rowIds.length) {
        await Promise.all(rowIds.map((id) => updateMaster(id, { isActive })));
        return;
      }
      await toggleRentalPackageStatus({
        zoneCode: group.zoneCode,
        packageGroupCode: group.packageGroupCode,
        serviceSlug: saveServiceSlug(group),
        isActive,
      });
    },
    onMutate: async ({ group, isActive }) => {
      const groupKey = buildRentalPackageGroupKey(group);
      setTogglingGroupKey(groupKey);
      setStatusOverrides((prev) => ({ ...prev, [groupKey]: isActive }));
      await queryClient.cancelQueries({ queryKey: packagesQueryKey });
      const previous = queryClient.getQueryData<RentalPackageGroup[]>(packagesQueryKey);
      if (previous) {
        queryClient.setQueryData(
          packagesQueryKey,
          patchRentalPackageGroups(previous, group, { isActive }),
        );
      }
      return { previous, groupKey };
    },
    onSuccess: (_data, { group, isActive }) => {
      queryClient.setQueryData<RentalPackageGroup[] | undefined>(
        packagesQueryKey,
        (current) => (current ? patchRentalPackageGroups(current, group, { isActive }) : current),
      );
      queryClient.invalidateQueries({ queryKey: ['rental-zones'] });
      queryClient.invalidateQueries({ queryKey: ['rental-zones-hierarchy'] });
    },
    onError: (err: unknown, { group }, context) => {
      const groupKey = context?.groupKey ?? buildRentalPackageGroupKey(group);
      if (context?.previous) {
        queryClient.setQueryData(packagesQueryKey, context.previous);
      }
      setStatusOverrides((prev) => {
        const next = { ...prev };
        delete next[groupKey];
        return next;
      });
      setToast({ msg: apiErrorMessage(err, 'Failed to update status'), severity: 'error' });
    },
    onSettled: (_data, _error, { group }) => {
      const groupKey = buildRentalPackageGroupKey(group);
      setTogglingGroupKey(null);
      setStatusOverrides((prev) => {
        const next = { ...prev };
        delete next[groupKey];
        return next;
      });
    },
  });

  const handleExport = () => {
    const rows = allGroups.flatMap((g) =>
      g.vehiclePrices.map((vp) => ({
        packageGroupCode: g.packageGroupCode,
        zoneCode: g.zoneCode,
        packageHr: g.packageHr,
        packageKm: g.packageKm,
        vehicleType: vp.vehicleTypeCode,
        price: vp.price,
        graceTimeMin: vp.graceTimeMin,
        extraKmPrice: vp.extraKmPrice,
      })),
    );
    const csv = rowsToCsv(rows, [
      { key: 'packageGroupCode', label: 'Package Group' },
      { key: 'zoneCode', label: 'Zone' },
      { key: 'packageHr', label: 'Hours' },
      { key: 'packageKm', label: 'KM' },
      { key: 'vehicleType', label: 'Vehicle Type' },
      { key: 'price', label: 'Price' },
      { key: 'graceTimeMin', label: 'Grace (min)' },
      { key: 'extraKmPrice', label: 'Extra KM Price' },
    ]);
    downloadListCsv(csv, `${effectiveZone || activeServiceSlug || 'all'}-rental-packages`);
  };

  const label = title || (
    unifiedTransportRental ? 'Rental Packages' :
    activeServiceSlug === 'bike-rental' ? 'Bike Rental' :
    activeServiceSlug === 'car-rental' ? 'Car Rental' : 'Rental Master'
  );

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} sx={{ mb: 2 }} spacing={1}>
        <Box>
          <Typography variant="h6" fontWeight={800}>{translateUiText(label, t)}</Typography>
          <Typography variant="body2" color="text.primary" sx={{ opacity: 0.85, lineHeight: 1.55, maxWidth: 720 }}>
            {translateUiText(subtitle || (
              unifiedTransportRental
                ? 'Zone-wise hour/km packages for all transport rental services — add pricing and time details from one screen.'
                : 'Zone-wise hour/km packages — prices used at booking when zone + package match.'
            ), t)}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button size="small" variant="outlined" startIcon={<FileDownload />} onClick={handleExport} disabled={!allGroups.length}>
            Export
          </Button>
          <Button size="small" variant="contained" startIcon={<Add />} onClick={() => { setEditingGroup(null); setDrawerOpen(true); }}>
            Add Package
          </Button>
        </Stack>
      </Stack>

      {!unifiedTransportRental && !hideServiceFilter && !isDedicatedRental && (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
          <Chip label="All rental services" onClick={() => { setServiceFilter('all'); setPage(0); }} color={serviceFilter === 'all' ? 'primary' : 'default'} variant={serviceFilter === 'all' ? 'filled' : 'outlined'} />
          {ALL_RENTAL_SLUGS.map((slug) => (
            <Chip key={slug} label={slug} onClick={() => { setServiceFilter(slug); setPage(0); }} color={serviceFilter === slug ? 'primary' : 'default'} variant={serviceFilter === slug ? 'filled' : 'outlined'} />
          ))}
        </Stack>
      )}

      {!lockedZoneCode && (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
          <Chip label="All zones" onClick={() => { setZoneFilter(''); setPage(0); }} color={!zoneFilter ? 'primary' : 'default'} variant={!zoneFilter ? 'filled' : 'outlined'} />
          {zones.map((z) => (
            <Chip
              key={z._id}
              label={z.name}
              onClick={() => { setZoneFilter(z.code); setPage(0); }}
              color={zoneFilter === z.code ? 'primary' : 'default'}
              variant={zoneFilter === z.code ? 'filled' : 'outlined'}
            />
          ))}
        </Stack>
      )}

      {isLoading ? (
        <Stack spacing={1}>{[1, 2, 3].map((i) => <Skeleton key={i} height={52} />)}</Stack>
      ) : !allGroups.length ? (
        <Paper elevation={0} sx={{ py: 6, textAlign: 'center', border: 1, borderColor: 'divider', borderStyle: 'dashed', borderRadius: 2.5 }}>
          <Garage sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography variant="subtitle1" fontWeight={700} color="text.primary">No rental packages</Typography>
          <Typography variant="body2" color="text.primary" sx={{ mb: 2, opacity: 0.82 }}>
            Seed data or create hour/km packages for this zone.
          </Typography>
          <Button variant="contained" startIcon={<Add />} onClick={() => { setEditingGroup(null); setDrawerOpen(true); }}>
            Add First Package
          </Button>
        </Paper>
      ) : (
        <Paper elevation={0} sx={{ borderRadius: 2.5, border: 1, borderColor: 'divider', overflow: 'hidden' }}>
          <TableContainer>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, width: 56, bgcolor: 'background.paper', color: 'text.primary' }}>S.No</TableCell>
                  <TableCell sx={{ fontWeight: 700, bgcolor: 'background.paper', color: 'text.primary' }}>Package</TableCell>
                  {!lockedZoneCode && <TableCell sx={{ fontWeight: 700, bgcolor: 'background.paper', color: 'text.primary' }}>Zone</TableCell>}
                  <TableCell sx={{ fontWeight: 700, bgcolor: 'background.paper', color: 'text.primary' }}>Vehicles & Pricing</TableCell>
                  <TableCell sx={{ fontWeight: 700, bgcolor: 'background.paper', color: 'text.primary' }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 700, minWidth: 160, bgcolor: 'background.paper', color: 'text.primary' }} align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pagedGroups.map((group, index) => {
                  const groupKey = buildRentalPackageGroupKey(group);
                  const isActive = resolveGroupActive(group);
                  return (
                  <TableRow key={groupKey} hover sx={{ '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.03) } }}>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600} color="text.secondary">{page * pageSize + index + 1}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={700} color="text.primary">{group.packageHr}h / {group.packageKm}km</Typography>
                      <Typography variant="caption" color="text.primary" sx={{ opacity: 0.72 }}>{group.packageGroupCode}</Typography>
                    </TableCell>
                    {!lockedZoneCode && (
                      <TableCell>
                        <Typography variant="body2" fontWeight={600} color="text.primary">{group.zoneName}</Typography>
                      </TableCell>
                    )}
                    <TableCell>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                        {group.vehiclePrices.map((vp) => (
                          <Chip
                            key={vp.vehicleTypeCode}
                            size="small"
                            variant="outlined"
                            label={`${vp.vehicleTypeName} ${formatMoney(vp.price)}`}
                            sx={{ fontWeight: 600, color: 'text.primary', borderColor: alpha(theme.palette.text.primary, 0.22) }}
                          />
                        ))}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Switch
                          size="small"
                          checked={isActive}
                          disabled={togglingGroupKey === groupKey}
                          onChange={(e) => toggleMutation.mutate({ group, isActive: e.target.checked })}
                        />
                        <Chip size="small" label={isActive ? 'Active' : 'Inactive'} color={isActive ? 'success' : 'default'} />
                      </Stack>
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<Edit fontSize="small" />}
                          onClick={() => { setEditingGroup(group); setDrawerOpen(true); }}
                        >
                          Edit
                        </Button>
                        <Button
                          size="small"
                          color="error"
                          variant="outlined"
                          startIcon={<Delete fontSize="small" />}
                          onClick={() => {
                            if (window.confirm('Delete this package and all vehicle prices?')) {
                              deleteMutation.mutate(group);
                            }
                          }}
                        >
                          Delete
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={allGroups.length}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={pageSize}
            onRowsPerPageChange={(e) => { setPageSize(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[10, 25, 50]}
          />
        </Paper>
      )}

      <RentalPackageDrawer
        open={drawerOpen}
        onClose={() => { setDrawerOpen(false); setEditingGroup(null); }}
        editingGroup={editingGroup}
        zones={drawerZones}
        existingGroups={allGroups}
        vehicleTypes={vehicleTypeRows.map((v) => ({
          code: v.code,
          name: v.name,
          serviceTypes: String(v.metadata?.serviceTypes || ''),
        }))}
        serviceSlug={drawerServiceSlug}
        unifiedTransportRental={unifiedTransportRental}
        lockedZoneCode={lockedZoneCode}
        lockedZoneName={resolvedLockedZoneName}
        defaultZoneCode={!lockedZoneCode ? zoneFilter : undefined}
        loading={saveMutation.isPending}
        onSave={(form, group) => saveMutation.mutate({ form, group })}
      />

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {toast ? <Alert severity={toast.severity} onClose={() => setToast(null)}>{toast.msg}</Alert> : undefined}
      </Snackbar>
    </Box>
  );
}
